import { handleAddToCollectionOp } from "@sg/query";
import { useMutation, useQuery } from "react-query";
import { z } from "zod";
import { useDefaultMarginGroup } from "~/pages/Counterparty/hooks";
import { AllocationType, TransferStatus, TransferType } from "~/types";
import { agent } from "~/util/agent";

const QUERY_TRANSFERS_KEY = "transfers";

const TransferSchema = z.object({
  sender_counterparty_id: z.string().optional(),
  sender_margin_group_id: z.string().optional().nullable(),
  receiver_counterparty_id: z.string().optional(),
  receiver_margin_group_id: z.string().optional().nullable(),
  amount: z.string(),
  currency: z.string(),
  allocation_type: z.nativeEnum(AllocationType),
  external: z.boolean().optional().nullable(),
  type: z.nativeEnum(TransferType),
  pledged_away: z.boolean().optional(),
  details: z
    .object({
      wallet_address: z.string().optional(),
      transaction_hash: z.string().optional(),
    })
    .optional()
    .nullable(),
  status: z.nativeEnum(TransferStatus).optional().nullable(),
  reference_transaction_id: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  sender_counterparty_name: z.string().optional().nullable(),
  receiver_counterparty_name: z.string().optional().nullable(),
  created_by: z.string().optional().nullable(),
  created_date: z.string().optional().nullable(),
  send_date: z.string().optional().nullable(),
  updated_by: z.string().optional().nullable(),
  update_date: z.string().optional().nullable(),
  id: z.string().optional().nullable(),
  tri_party: z.boolean().optional(),
  aca: z.boolean().optional(),
  tri_party_details: z.string().optional(),
});

type TransferResourceType = z.infer<typeof TransferSchema>;

const useTransfers = () => {
  const group = useDefaultMarginGroup();
  return useQuery(
    [QUERY_TRANSFERS_KEY, "group", group?.id],
    async () => agent(`transfers/?margin_group_id=${group?.id}`).json<TransferResourceType[]>(),
    {
      enabled: !!group,
    }
  );
};

const useTransfer = (transferId?: string) => {
  return useQuery(
    [QUERY_TRANSFERS_KEY, transferId],
    async () => agent(`transfers/${transferId}`).json<TransferResourceType>(),
    {
      enabled: !!transferId,
    }
  );
};

const useCreateTransfer = () => {
  const group = useDefaultMarginGroup();
  return useMutation<unknown, Error, TransferResourceType>(
    (data) => {
      return agent.post(`transfers/`, { labels: ["created", "transfer"], json: data });
    },
    { ...handleAddToCollectionOp([QUERY_TRANSFERS_KEY, group?.id]) }
  );
};

export { QUERY_TRANSFERS_KEY, TransferSchema, useCreateTransfer, useTransfer, useTransfers };
export type { TransferResourceType };
